// API services for handling conversation and chat requests
import API_CONFIG from '../config';

export const createNewConversation = async (isAuthenticated, userId) => {
    try {
        if (!isAuthenticated) {
            const localConversationId = `local-${Date.now()}`;
            
            // Update URL with conversation ID for non-authenticated users
            window.history.pushState({ conversationId: localConversationId }, '', `/c/${localConversationId}`);

            const newConversation = {
                id: localConversationId,
                conversation_id: localConversationId,
                title: "New Conversation",
                created_at: new Date().toISOString()
            };

            return { conversation: newConversation, id: localConversationId };
        }

        const authToken = localStorage.getItem('authToken');
        if (!userId) {
            throw new Error('User ID not available. Please log in again.');
        }

        // Import the API client dynamically to avoid circular dependency
        const { apiClient } = await import('./apiClient');
        
        const response = await apiClient.post(`${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.CREATE}`, {
            title: "New Conversation",
            user_id: userId
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail?.[0]?.msg || 'Failed to create conversation');
        }

        const data = await response.json();
        
        // Update URL with conversation ID
        window.history.pushState({ conversationId: data.id }, '', `/c/${data.id}`);

        // Create a fully formed conversation object
        const newConversation = {
            id: data.id,
            conversation_id: data.id,
            title: "New Conversation",
            updated_at: new Date().toISOString(),
            user_id: userId
        };

        return { conversation: newConversation, id: data.id };
    } catch (error) {
        console.error('Error creating conversation:', error);
        return null;
    }
};

export const sendChatMessage = async ({
    endpoint,
    requestBody,
    headers,
    abortController
}) => {
    const response = await fetch(endpoint, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(requestBody),
        signal: abortController.signal
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail?.[0]?.msg || errorData.error || 'Failed to process request');
    }

    return response;
};

export const sendStopCommand = async (currentConversationId, isAuthenticated, userId) => {
    try {
        if (currentConversationId && isAuthenticated) {
            const authToken = localStorage.getItem('authToken');
            const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.CHAT}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    conversation_id: currentConversationId,
                    user_id: userId,
                    cancel: true, // Flag to indicate cancellation
                }),
            });

            if (!response.ok) {
                console.error('Failed to send cancellation request:', response.statusText);
            }
        } else {
            console.warn('No conversation ID or not authenticated, skipping cancellation');
        }
    } catch (err) {
        console.error('Error handling stop command:', err);
    }
};

export const loadConversationFromUrl = async () => {
    const path = window.location.pathname;
    const match = path.match(/\/c\/([^\/]+)/);

    if (match && match[1]) {
        const conversationId = match[1];

        try {
            // Import the API client dynamically to avoid circular dependency
            const { apiClient } = await import('./apiClient');
            
            const response = await apiClient.post(`${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.GET_CHAT}`, {
                conversation_id: conversationId
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch conversation: ${response.status}`);
            }

            const data = await response.json();
            return {
                conversationId,
                chatHistory: data.chat_history || []
            };
        } catch (error) {
            console.error('Error loading conversation:', error);
            return { conversationId, chatHistory: [] };
        }
    }

    return null;
};

export const updateConversationTitle = async (conversationId, newTitle, userId) => {
    

    
    if (!userId) throw new Error('Not authenticated');
    
    // Import the API client dynamically to avoid circular dependency
    const { apiClient } = await import('./apiClient');
    
    const response = await apiClient.post(`${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.UPDATE_TITLE}`, {
        conversation_id: conversationId,
        title: newTitle,
        user_id: userId,
    });
    

    
    if (!response.ok) {
        const errorData = await response.json();
        console.error('API Error response:', errorData);
        throw new Error(errorData.detail?.[0]?.msg || errorData.error || 'Failed to update title');
    }
    
    const responseData = await response.json();

    return responseData;
};

export const debugConversations = async (userId) => {
    const authToken = localStorage.getItem('authToken');
    if (!authToken || !userId) throw new Error('Not authenticated');
    
    const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.DEBUG}`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            user_id: userId
        })
    });
    
    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail?.[0]?.msg || errorData.error || 'Failed to debug conversations');
    }
    
    return await response.json();
};

// Build request configuration based on mode and content
export const buildRequestConfig = ({
    isAuthenticated,
    token,
    userId,
    isGraphingMode,
    isChartAnalysisMode,
    uploadedFile,
    trimmedInput,
    conversationIdToUse,
    currentChartData,
    currentPlotConfig,
    chartType
}) => {
    let endpoint;
    let requestBody;
    let headers = {
        'Content-Type': 'application/json',
    };

    // Add authorization header only if authenticated
    if (isAuthenticated && token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    if (isGraphingMode) {
        endpoint = `${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.GRAPH}`;

        if (isChartAnalysisMode && currentChartData && currentPlotConfig) {
            // Chart analysis mode
            requestBody = {
                mode: 'analyze',
                query: trimmedInput,
                chartData: currentChartData,
                plotConfig: currentPlotConfig
            };
        } else {
            // Chart generation mode
            requestBody = {
                mode: 'generate',
                csvData: uploadedFile?.content,
                chartType: chartType
            };
        }
    } else if (uploadedFile && uploadedFile.type === 'image') {
        endpoint = `${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.IMAGE}`;
        requestBody = {
            query: trimmedInput,
            image_url: uploadedFile.public_url,
            conversation_id: isAuthenticated ? conversationIdToUse : null
        };
    } else if (uploadedFile && uploadedFile.type === 'pdf') {
        // Use regular chat endpoint but include PDF content
        if (isAuthenticated) {
            endpoint = `${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.CHAT}`;
            requestBody = {
                query: trimmedInput,
                conversation_id: conversationIdToUse,
                pdfContent: uploadedFile.content // Include PDF content in the request
            };
        } else {
            endpoint = `${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.CUSTOM_CHAT}`;
            requestBody = {
                query: trimmedInput,
                pdfContent: uploadedFile.content // Include PDF content in the request
            };
        }
    } else {
        if (isAuthenticated) {
            endpoint = `${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.CHAT}`;
            requestBody = {
                query: trimmedInput,
                conversation_id: conversationIdToUse
            };
        } else {
            endpoint = `${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.CUSTOM_CHAT}`;
            requestBody = {
                query: trimmedInput
            };
        }
    }

    return { endpoint, requestBody, headers };
};

// Dispatch conversation update events
export const dispatchConversationEvent = (conversation, onConversationCreated) => {
    if (!conversation) return;
    
    // Create a custom event with conversation data
    const event = new CustomEvent('conversationCreated', {
        detail: conversation
    });

    // Dispatch the event for the Sidebar to listen for
    window.dispatchEvent(event);

    // Also call the callback if provided
    if (onConversationCreated) {
        onConversationCreated(conversation);
    }
}; 